/**
 * The storefront guest's identity, minted by the API — spec
 * docs/superpowers/specs/2026-09-26-guest-ordering-without-app-check-design.md.
 *
 * A guest used to be created in the browser by `signInAnonymously()`, which
 * goes through Identity Toolkit and therefore through Firebase App Check: a
 * browser App Check refused (a low reCAPTCHA score, or a reCAPTCHA script a
 * content blocker never let load) could not become a guest at all, so it had no
 * cart and could not pay.
 *
 * Now `establishGuestIdentity()` asks the API's mint
 * (`POST /api/v1/public/guest-sessions`) THROUGH the storefront's own
 * `/api/guest-session` route, which runs Vercel BotID first — BotID protects
 * only same-origin requests (see `@order-up/common` guestMintProxy.ts). The
 * mint creates a REAL Firebase anonymous user server-side and returns:
 *   - a custom token — signed in with as normal, so a browser that passes App
 *     Check is an ordinary Firebase guest and nothing downstream changes;
 *   - a guest session for the same uid — kept in localStorage, and USED (as the
 *     API Bearer) while the custom-token sign-in runs and whenever it fails on
 *     this page; a Firebase user, once signed in, always takes precedence.
 *
 * One identity either way: the uid is the Firebase uid. When the platform
 * switch is off the mint answers 404 and the caller signs in anonymously in the
 * browser, exactly as before.
 */
import { foldLocationResponseNames, GUEST_MINT_PATH } from '@order-up/common';
import { createLogger } from '../utils/logger';

const logger = createLogger('GuestSession');

const STORAGE_KEY = 'orderup.guestSession.v1';

/**
 * How long the custom-token sign-in may take before the page proceeds on the
 * guest session. A refused browser's sign-in either rejects or waits on a
 * reCAPTCHA run that will not produce a token; the guest must not wait on it.
 * If it completes later, the Firebase user (the same uid) simply takes over.
 */
export const GUEST_SIGN_IN_TIMEOUT_MS = 6_000;

/**
 * How long the mint (BotID challenge + the API) may take. If BotID's challenge
 * script never loads, its fetch wrapper would wait forever; past this the guest
 * falls back to `signInAnonymously()` rather than having no identity at all.
 */
export const GUEST_MINT_TIMEOUT_MS = 10_000;

export interface StoredGuestSession {
  readonly uid: string;
  readonly token: string;
  /** Epoch ms. */
  readonly expiresAt: number;
}

interface MintResponse {
  uid: string;
  customToken: string;
  guestSession: string;
  expiresAt: number;
  resumed?: boolean;
}

/** The guest session this PAGE is using as its credential (no Firebase user). */
let active: StoredGuestSession | null = null;
const listeners = new Set<(session: StoredGuestSession | null) => void>();

function notify(): void {
  listeners.forEach((listener) => {
    try {
      listener(active);
    } catch {
      // A listener must never break the identity flow.
    }
  });
}

function readStored(now: number = Date.now()): StoredGuestSession | null {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<StoredGuestSession>;
    if (typeof parsed.uid !== 'string' || typeof parsed.token !== 'string' || typeof parsed.expiresAt !== 'number') {
      return null;
    }
    return parsed.expiresAt > now ? (parsed as StoredGuestSession) : null;
  } catch {
    return null;
  }
}

/** Persist a session for later resumes. Storage can be unavailable (private mode); then it lives for this page only. */
export function storeGuestSession(session: StoredGuestSession): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(session));
  } catch {
    // Not persisted; the page still holds it while active.
  }
}

/** Use this session as the page's credential (the browser could not sign in to Firebase). */
export function activateGuestSession(session: StoredGuestSession): void {
  active = session;
  notify();
}

/** Stop using the guest session (a Firebase user now exists, or the guest signed out). */
export function deactivateGuestSession(): void {
  if (active === null) return;
  active = null;
  notify();
}

/** The active guest session, if this page is running on one and it has not expired. */
export function getActiveGuestSession(now: number = Date.now()): StoredGuestSession | null {
  return active && active.expiresAt > now ? active : null;
}

/** The Bearer for the API client, when this page is running on a guest session. */
export function getGuestSessionBearer(): string | null {
  return getActiveGuestSession()?.token ?? null;
}

export function subscribeGuestSession(listener: (session: StoredGuestSession | null) => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

let botIdReady: Promise<void> | null = null;

/**
 * Install Vercel BotID's request wrapper for the mint route, once per page,
 * BEFORE the first mint — so no app can mint ahead of it. A failure to load is
 * logged and the mint still goes out; the API then decides (it refuses an
 * unchecked request only while BotID is enforced).
 */
function ensureBotId(): Promise<void> {
  if (!botIdReady) {
    botIdReady = import('botid/client/core')
      .then(({ initBotId }) => {
        initBotId({ protect: [{ path: GUEST_MINT_PATH, method: 'POST' }] });
      })
      .catch((error: unknown) => {
        logger.warn('BotID client failed to load; minting without it', {
          error: error instanceof Error ? error.message : String(error),
        });
      });
  }
  return botIdReady;
}

class MintError extends Error {
  constructor(readonly status: number, readonly code: string | undefined) {
    super(`guest-session mint answered HTTP ${status}${code ? ` (${code})` : ''}`);
  }
}

/**
 * Ask for a guest through this storefront's BotID-checked route. Returns `null`
 * when guest sessions are switched off (404) — the caller then signs in
 * anonymously in the browser.
 */
async function mint(): Promise<MintResponse | null> {
  await ensureBotId();
  const stored = readStored();
  const response = await withTimeout(
    fetch(GUEST_MINT_PATH, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(stored ? { guestSession: stored.token } : {}),
      credentials: 'same-origin',
    }),
    GUEST_MINT_TIMEOUT_MS,
  );
  if (response.status === 404) return null;
  const envelope = foldLocationResponseNames(await response.json().catch(() => null)) as
    | { success?: boolean; data?: MintResponse; error?: { code?: string } }
    | null;
  if (!response.ok || !envelope?.success || !envelope.data) {
    throw new MintError(response.status, envelope?.error?.code);
  }
  return envelope.data;
}

export type GuestIdentityOutcome =
  /** Signed in to Firebase with the minted custom token: an ordinary guest. */
  | 'firebase'
  /** Firebase sign-in failed (App Check); the page runs on the guest session. */
  | 'guest-session'
  /** Guest sessions are off or the mint failed: sign in anonymously in the browser. */
  | 'fallback';

function withTimeout<T>(promise: Promise<T>, ms: number): Promise<T> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  return Promise.race([
    promise,
    new Promise<never>((_, reject) => {
      timer = setTimeout(() => reject(new Error(`guest sign-in timed out after ${ms}ms`)), ms);
    }),
  ]).finally(() => clearTimeout(timer));
}

/** Signs the browser in to Firebase with a custom token (the caller's auth service / mutation). */
export type SignInWithCustomToken = (customToken: string) => Promise<unknown>;

let inflight: Promise<GuestIdentityOutcome> | null = null;

/**
 * Make this browser a guest. Idempotent while running: concurrent callers share
 * one attempt.
 */
export function establishGuestIdentity(signIn: SignInWithCustomToken): Promise<GuestIdentityOutcome> {
  if (!inflight) {
    inflight = (async (): Promise<GuestIdentityOutcome> => {
      let minted: MintResponse | null;
      try {
        minted = await mint();
      } catch (error) {
        logger.warn('Guest session mint failed; signing in anonymously instead', {
          error: error instanceof Error ? error.message : String(error),
        });
        return 'fallback';
      }
      if (!minted) return 'fallback';

      const session: StoredGuestSession = { uid: minted.uid, token: minted.guestSession, expiresAt: minted.expiresAt };
      storeGuestSession(session);
      return adoptCustomTokenOrGuestSession(minted.customToken, session, signIn);
    })().finally(() => {
      inflight = null;
    });
  }
  return inflight;
}

/**
 * Sign in with a custom token for `session.uid`; if that fails or stalls (App
 * Check refused this browser), run the page on the guest session instead.
 * Shared by the storefront mint and checkout's hand-off redeem.
 */
export async function adoptCustomTokenOrGuestSession(
  customToken: string,
  session: StoredGuestSession | null,
  signIn: SignInWithCustomToken,
): Promise<'firebase' | 'guest-session'> {
  // The page has an identity from the moment the session exists: run on it
  // WHILE the sign-in is attempted. A refused browser's sign-in can stall for
  // the whole timeout, and a guest who adds an item or taps Checkout in that
  // window must not be told they are signed out (prod 2026-10-12). The uid is
  // the same either way; a successful sign-in hands over to the Firebase user.
  if (session) activateGuestSession(session);
  try {
    await withTimeout(signIn(customToken), GUEST_SIGN_IN_TIMEOUT_MS);
    deactivateGuestSession();
    return 'firebase';
  } catch (error) {
    if (!session) throw error;
    logger.warn('Firebase sign-in failed for a minted guest; continuing on the guest session', {
      uid: session.uid,
      error: error instanceof Error ? error.message : String(error),
    });
    return 'guest-session';
  }
}

/** Test seam. */
export function __resetGuestSessionForTests(): void {
  active = null;
  inflight = null;
  botIdReady = null;
  listeners.clear();
}
